/**
 * Retry tracking utility for managing error state refresh attempts
 * Falls back to null state after max retries within a time window
 */

const STORAGE_KEY = 'projects_overview_retry_tracker';
const MAX_RETRIES = 10;
const TIME_WINDOW_HOURS = 24; // 24 hours window
const TIME_WINDOW_MS = TIME_WINDOW_HOURS * 60 * 60 * 1000;

/**
 * Check if localStorage is available (browser environment)
 */
const isLocalStorageAvailable = () => {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch (error) {
    return false;
  }
};

/**
 * Get current retry data from storage
 */
const getRetryData = () => {
  try {
    if (!isLocalStorageAvailable()) {
      return { count: 0, timestamps: [] };
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { count: 0, timestamps: [] };
    }
    const parsed = JSON.parse(stored);

    // Validate shape
    if (
      typeof parsed === 'object'
      && parsed !== null
      && typeof parsed.count === 'number'
      && Array.isArray(parsed.timestamps)
    ) {
      return parsed;
    }

    // Invalid shape, return default
    return { count: 0, timestamps: [] };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to read retry data:', error);
    return { count: 0, timestamps: [] };
  }
};

/**
 * Save retry data to storage
 */
const saveRetryData = (data) => {
  try {
    if (!isLocalStorageAvailable()) {
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save retry data:', error);
  }
};

/**
 * Filter timestamps to only include those within the time window
 */
const filterRecentTimestamps = (timestamps) => {
  const now = Date.now();
  return timestamps.filter((ts) => (now - ts) < TIME_WINDOW_MS);
};

/**
 * Check if max retries have been exceeded
 * @returns {boolean} true if max retries exceeded, false otherwise
 */
export const hasExceededMaxRetries = () => {
  const data = getRetryData();
  const recentTimestamps = filterRecentTimestamps(data.timestamps);
  return recentTimestamps.length >= MAX_RETRIES;
};

/**
 * Record a retry attempt
 */
export const recordRetryAttempt = () => {
  const data = getRetryData();
  const now = Date.now();

  // Filter to keep only recent timestamps and add the new one
  const recentTimestamps = filterRecentTimestamps(data.timestamps);
  recentTimestamps.push(now);

  const newData = {
    count: recentTimestamps.length,
    timestamps: recentTimestamps
  };

  saveRetryData(newData);

  return newData.count;
};

/**
 * Reset retry tracking (e.g., after successful load)
 */
export const resetRetryTracking = () => {
  try {
    if (!isLocalStorageAvailable()) {
      return;
    }
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to reset retry tracking:', error);
  }
};

/**
 * Get current retry count within time window
 */
export const getRetryCount = () => {
  const data = getRetryData();
  const recentTimestamps = filterRecentTimestamps(data.timestamps);
  return recentTimestamps.length;
};

/**
 * Get retry statistics for debugging
 */
export const getRetryStats = () => {
  const data = getRetryData();
  const recentTimestamps = filterRecentTimestamps(data.timestamps);

  return {
    currentCount: recentTimestamps.length,
    maxRetries: MAX_RETRIES,
    timeWindowHours: TIME_WINDOW_HOURS,
    hasExceeded: recentTimestamps.length >= MAX_RETRIES,
    remainingRetries: Math.max(0, MAX_RETRIES - recentTimestamps.length)
  };
};
